import {
  getFirestore,
  collection,
  doc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  onSnapshot,
  writeBatch,
  runTransaction,
  Timestamp,
  increment
} from 'firebase/firestore';

import { APP_ID } from '../config/constants';

const SOURCES_COLLECTION = 'sources';
const TRANSACTIONS_COLLECTION = 'transactions';

/**
 * Get Firestore instance
 */
const getDb = () => {
  return getFirestore();
};

/**
 * Get sources collection reference for a specific user
 */
const getSourcesRef = (userId) => {
  const db = getDb();
  return collection(db, 'artifacts', APP_ID, 'users', userId, SOURCES_COLLECTION);
};

/**
 * Get transactions collection reference for a specific user
 */
const getTransactionsRef = (userId) => {
  const db = getDb();
  return collection(db, 'artifacts', APP_ID, 'users', userId, TRANSACTIONS_COLLECTION);
};

/**
 * Add new source
 */
export const addSource = async (userId, sourceData) => {
  try {
    const sourcesRef = getSourcesRef(userId);
    const docRef = await addDoc(sourcesRef, {
      ...sourceData,
      balance: sourceData.initialBalance || 0,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error adding source:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update existing source
 */
export const updateSource = async (userId, sourceId, updates) => {
  try {
    const db = getDb();
    const sourceRef = doc(db, 'artifacts', APP_ID, 'users', userId, SOURCES_COLLECTION, sourceId);
    await updateDoc(sourceRef, {
      ...updates,
      updatedAt: Timestamp.now()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating source:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete source
 */
export const deleteSource = async (userId, sourceId) => {
  try {
    const db = getDb();
    const sourceRef = doc(db, 'artifacts', APP_ID, 'users', userId, SOURCES_COLLECTION, sourceId);
    await deleteDoc(sourceRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting source:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all sources for a user
 */
export const getSources = async (userId) => {
  try {
    const sourcesRef = getSourcesRef(userId);
    const q = query(sourcesRef);
    
    const snapshot = await getDocs(q);
    const sources = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return { success: true, data: sources };
  } catch (error) {
    console.error('Error getting sources:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Real-time listener for sources
 */
export const subscribeToSources = (userId, callback) => {
  try {
    const sourcesRef = getSourcesRef(userId);
    const q = query(sourcesRef);
    
    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const sources = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        callback({ success: true, data: sources });
      },
      (error) => {
        console.error('Error in source subscription:', error);
        callback({ success: false, error: error.message, data: [] });
      }
    );
    
    return unsubscribe;
  } catch (error) {
    console.error('Error subscribing to sources:', error);
    return () => {};
  }
};

/**
 * Update source balance
 * @param {string} userId - User ID
 * @param {string} sourceId - Source ID
 * @param {number} amount - Amount to add (positive) or subtract (negative)
 * @param {string} transactionType - 'income' or 'expense'
 * @param {string} transactionId - Related transaction ID
 * @param {string} description - Transaction description
 */
export const updateBalance = async (userId, sourceId, amount, transactionType, transactionId, description) => {
  try {
    const db = getDb();
    const sourceRef = doc(db, 'artifacts', APP_ID, 'users', userId, SOURCES_COLLECTION, sourceId);
    
    // Update balance using increment
    await updateDoc(sourceRef, {
      balance: increment(amount),
      updatedAt: Timestamp.now()
    });

    // Track transaction history
    await trackTransaction(userId, {
      sourceId,
      amount,
      type: transactionType,
      relatedId: transactionId,
      description,
      timestamp: Timestamp.now()
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating balance:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Điều chỉnh số dư về giá trị thực tế (đối soát / reconciliation).
 *
 * Đây là đường DUY NHẤT hợp lệ để sửa tay số dư — luôn để lại vết audit
 * (số cũ, số mới, chênh lệch, lý do bắt buộc). Với thẻ tín dụng, điều chỉnh
 * usedCredit thay vì balance.
 *
 * @param {string} userId
 * @param {string} sourceId
 * @param {number} actualValue - Số dư thực tế (hoặc dư nợ thực tế với thẻ)
 * @param {string} reason - Lý do điều chỉnh (bắt buộc)
 */
export const adjustBalance = async (userId, sourceId, actualValue, reason) => {
  try {
    if (!reason || !reason.trim()) {
      return { success: false, error: 'Cần nhập lý do điều chỉnh' };
    }
    if (typeof actualValue !== 'number' || Number.isNaN(actualValue) || actualValue < 0) {
      return { success: false, error: 'Giá trị điều chỉnh không hợp lệ' };
    }

    const db = getDb();
    const sourceRef = doc(db, 'artifacts', APP_ID, 'users', userId, SOURCES_COLLECTION, sourceId);

    let auditInfo = null;
    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(sourceRef);
      if (!snap.exists()) throw new Error('Không tìm thấy nguồn tiền');

      const data = snap.data();
      const isCredit = data.type === 'credit';
      const field = isCredit ? 'usedCredit' : 'balance';
      const oldValue = Number(data[field]) || 0;
      const difference = actualValue - oldValue;

      const updates = {
        [field]: actualValue,
        updatedAt: Timestamp.now()
      };
      // P7: điều chỉnh giá trị tài sản dài hạn = một lần định giá lại
      if (['savings', 'investment', 'asset'].includes(data.type)) {
        updates.valuationDate = new Date().toISOString().split('T')[0];
      }
      transaction.update(sourceRef, updates);

      // Vết audit ghi trong cùng transaction — điều chỉnh không bao giờ câm lặng
      const auditRef = doc(getTransactionsRef(userId));
      transaction.set(auditRef, {
        sourceId,
        amount: isCredit ? -difference : difference, // quy về tác động lên tài sản ròng
        type: 'adjustment',
        oldValue,
        newValue: actualValue,
        field,
        reason: reason.trim(),
        description: `Điều chỉnh ${isCredit ? 'dư nợ thẻ' : 'số dư'} ${data.name || ''}: ${oldValue.toLocaleString('vi-VN')} → ${actualValue.toLocaleString('vi-VN')} (${reason.trim()})`,
        timestamp: Timestamp.now(),
        createdAt: Timestamp.now()
      });

      auditInfo = { oldValue, newValue: actualValue, difference, field };
    });

    return { success: true, data: auditInfo };
  } catch (error) {
    console.error('Error adjusting balance:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Khôi phục nguồn tiền vừa xoá với ĐÚNG document id cũ (dùng cho Undo).
 * Trước đây Undo tạo id mới khiến mọi giao dịch/nợ tham chiếu id cũ thành mồ côi.
 */
export const restoreSource = async (userId, sourceId, sourceData) => {
  try {
    const db = getDb();
    const sourceRef = doc(db, 'artifacts', APP_ID, 'users', userId, SOURCES_COLLECTION, sourceId);
    await setDoc(sourceRef, {
      ...sourceData,
      updatedAt: Timestamp.now()
    });
    return { success: true, id: sourceId };
  } catch (error) {
    console.error('Error restoring source:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Track transaction for audit trail
 */
export const trackTransaction = async (userId, transactionData) => {
  try {
    const transactionsRef = getTransactionsRef(userId);
    await addDoc(transactionsRef, {
      ...transactionData,
      createdAt: Timestamp.now()
    });
    return { success: true };
  } catch (error) {
    console.error('Error tracking transaction:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get transaction history for a source
 */
export const getTransactionHistory = async (userId, sourceId = null, limit = 50) => {
  try {
    const transactionsRef = getTransactionsRef(userId);
    let q = query(transactionsRef);
    
    const snapshot = await getDocs(q);
    let transactions = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Filter by sourceId if provided
    if (sourceId) {
      transactions = transactions.filter(t => t.sourceId === sourceId);
    }

    // Sort by timestamp descending
    transactions.sort((a, b) => {
      const aTime = a.timestamp?.toMillis() || 0;
      const bTime = b.timestamp?.toMillis() || 0;
      return bTime - aTime;
    });

    // Limit results
    transactions = transactions.slice(0, limit);

    return { success: true, data: transactions };
  } catch (error) {
    console.error('Error getting transaction history:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Get balance history statistics
 */
export const getBalanceStats = async (userId) => {
  try {
    const sourcesResult = await getSources(userId);
    if (!sourcesResult.success) {
      return { success: false, error: sourcesResult.error };
    }

    const sources = sourcesResult.data;
    
    // Separate credit cards from regular sources
    const regularSources = sources.filter(s => s.type !== 'credit');
    const creditCards = sources.filter(s => s.type === 'credit');
    
    // Total balance: only wallet, bank, cash (NOT credit cards)
    const totalBalance = regularSources.reduce((sum, s) => sum + (s.balance || 0), 0);
    const totalInitial = regularSources.reduce((sum, s) => sum + (s.initialBalance || 0), 0);
    
    // Credit card stats
    const totalCreditLimit = creditCards.reduce((sum, s) => sum + (s.creditLimit || 0), 0);
    const totalCreditUsed = creditCards.reduce((sum, s) => sum + (s.usedCredit || 0), 0);
    const totalCreditAvailable = totalCreditLimit - totalCreditUsed;
    
    // Net worth = assets - debt
    const netWorth = totalBalance - totalCreditUsed;
    
    const change = totalBalance - totalInitial;
    const changePercent = totalInitial > 0 ? ((change / totalInitial) * 100) : 0;

    const byType = sources.reduce((acc, s) => {
      const type = s.type || 'other';
      if (!acc[type]) {
        acc[type] = { count: 0, balance: 0 };
      }
      acc[type].count += 1;
      
      // For credit cards, use available credit instead of balance
      if (type === 'credit') {
        acc[type].balance += (s.creditLimit || 0) - (s.usedCredit || 0);
      } else {
        acc[type].balance += s.balance || 0;
      }
      return acc;
    }, {});

    return {
      success: true,
      data: {
        totalBalance,        // Total assets (wallet + bank + cash)
        totalInitial,
        change,
        changePercent,
        totalCreditLimit,    // Total credit limit
        totalCreditUsed,     // Total credit debt
        totalCreditAvailable, // Available credit
        netWorth,            // Assets - Debts
        totalSources: sources.length,
        activeSources: sources.filter(s => s.status === 'active').length,
        creditCardsCount: creditCards.length,
        byType
      }
    };
  } catch (error) {
    console.error('Error getting balance stats:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Migrate old source data to new format
 */
export const migrateSource = async (userId, sourceId, oldData) => {
  try {
    const newData = {
      name: oldData.name || 'Unknown',
      type: oldData.type || 'wallet',
      balance: oldData.amount || 0,
      initialBalance: oldData.amount || 0,
      currency: 'VND',
      icon: oldData.icon || 'wallet',
      color: oldData.color || '#6366f1',
      note: oldData.note || '',
      status: 'active',
      // Preserve existing timestamps if available
      createdAt: oldData.createdAt || Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    await updateSource(userId, sourceId, newData);
    return { success: true };
  } catch (error) {
    console.error('Error migrating source:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Bulk migrate all sources
 */
export const bulkMigrateSources = async (userId) => {
  try {
    const sourcesResult = await getSources(userId);
    if (!sourcesResult.success) {
      return sourcesResult;
    }

    const sources = sourcesResult.data;
    let migratedCount = 0;

    for (const source of sources) {
      // Check if already migrated (has 'type' field)
      if (!source.type) {
        await migrateSource(userId, source.id, source);
        migratedCount++;
      }
    }

    return { success: true, migratedCount };
  } catch (error) {
    console.error('Error bulk migrating sources:', error);
    return { success: false, error: error.message };
  }
};

// ==================== CREDIT MANAGEMENT ====================

/**
 * Update credit usage for credit sources
 * @param {string} userId - User ID
 * @param {string} sourceId - Credit source ID
 * @param {number} amount - Amount to add (positive) or subtract (negative) from used credit
 * @param {string} transactionType - 'charge' or 'payment'
 * @param {string} transactionId - Related transaction ID
 */
export const updateCreditUsage = async (userId, sourceId, amount, transactionType, transactionId) => {
  try {
    const db = getDb();
    const sourceRef = doc(db, 'artifacts', APP_ID, 'users', userId, SOURCES_COLLECTION, sourceId);
    
    // Update usedCredit using increment
    // For charges (expenses): amount is positive (increase used credit)
    // For payments: amount is negative (decrease used credit)
    await updateDoc(sourceRef, {
      usedCredit: increment(amount),
      updatedAt: Timestamp.now()
    });

    // Track transaction
    await trackTransaction(userId, {
      sourceId,
      amount,
      type: transactionType,
      relatedId: transactionId,
      description: `Credit ${transactionType}: ${amount}`,
      timestamp: Timestamp.now()
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating credit usage:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Initialize credit fields for a source
 * @param {string} userId - User ID
 * @param {string} sourceId - Source ID
 * @param {Object} creditConfig - {creditLimit, billingCycle, paymentDueDay, minimumPaymentPercent}
 */
export const initializeCreditSource = async (userId, sourceId, creditConfig) => {
  try {
    const updates = {
      creditLimit: creditConfig.creditLimit || 0,
      usedCredit: creditConfig.usedCredit || 0,
      billingCycle: creditConfig.billingCycle || 'monthly', // monthly, biweekly
      paymentDueDay: creditConfig.paymentDueDay || 5, // Day of month (1-31)
      minimumPaymentPercent: creditConfig.minimumPaymentPercent || 5, // Percentage (5%, 10%, etc.)
      lastStatementDate: creditConfig.lastStatementDate || null,
      nextPaymentDueDate: creditConfig.nextPaymentDueDate || null,
      updatedAt: Timestamp.now()
    };

    await updateSource(userId, sourceId, updates);
    return { success: true };
  } catch (error) {
    console.error('Error initializing credit source:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Calculate credit statistics for a source
 * @param {Object} source - Source object with credit fields
 * @returns {Object} Credit statistics
 */
export const calculateCreditStats = (source) => {
  const creditLimit = source.creditLimit || 0;
  const usedCredit = source.usedCredit || 0;
  const availableCredit = Math.max(0, creditLimit - usedCredit);
  const utilizationRate = creditLimit > 0 ? (usedCredit / creditLimit) * 100 : 0;
  const minimumPayment = (usedCredit * (source.minimumPaymentPercent || 5)) / 100;

  // Determine health status
  let healthStatus = 'good';
  if (utilizationRate >= 90) healthStatus = 'critical';
  else if (utilizationRate >= 70) healthStatus = 'warning';
  else if (utilizationRate >= 50) healthStatus = 'caution';

  // Calculate days until payment due
  let daysUntilDue = null;
  if (source.nextPaymentDueDate) {
    const today = new Date();
    const dueDate = source.nextPaymentDueDate.toDate ? source.nextPaymentDueDate.toDate() : new Date(source.nextPaymentDueDate);
    daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
  }

  return {
    creditLimit,
    usedCredit,
    availableCredit,
    utilizationRate: Math.round(utilizationRate * 10) / 10, // Round to 1 decimal
    minimumPayment,
    healthStatus,
    daysUntilDue,
    isOverLimit: usedCredit > creditLimit
  };
};

/**
 * Get all credit sources with statistics
 * @param {string} userId - User ID
 * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
 */
export const getCreditSources = async (userId) => {
  try {
    const sourcesResult = await getSources(userId);
    if (!sourcesResult.success) {
      return sourcesResult;
    }

    const creditSources = sourcesResult.data
      .filter(s => s.type === 'credit')
      .map(source => ({
        ...source,
        stats: calculateCreditStats(source)
      }));

    return { success: true, data: creditSources };
  } catch (error) {
    console.error('Error getting credit sources:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Reset credit billing cycle
 * @param {string} userId - User ID
 * @param {string} sourceId - Credit source ID
 */
export const resetCreditCycle = async (userId, sourceId) => {
  try {
    const today = new Date();
    const db = getDb();
    const sourceRef = doc(db, 'artifacts', APP_ID, 'users', userId, SOURCES_COLLECTION, sourceId);
    
    // Get source to calculate next due date
    const sourceSnap = await getDocs(query(collection(db, 'artifacts', APP_ID, 'users', userId, SOURCES_COLLECTION)));
    const source = sourceSnap.docs.find(d => d.id === sourceId)?.data();
    
    if (!source) {
      throw new Error('Source not found');
    }

    // Calculate next payment due date
    const paymentDueDay = source.paymentDueDay || 5;
    const nextDueDate = new Date(today.getFullYear(), today.getMonth() + 1, paymentDueDay);

    await updateDoc(sourceRef, {
      lastStatementDate: Timestamp.fromDate(today),
      nextPaymentDueDate: Timestamp.fromDate(nextDueDate),
      updatedAt: Timestamp.now()
    });

    return { success: true, nextDueDate };
  } catch (error) {
    console.error('Error resetting credit cycle:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get credit alerts (high utilization, payment due soon, over limit)
 * @param {Array} creditSources - Array of credit sources with stats
 * @returns {Array} Alerts
 */
export const getCreditAlerts = (creditSources) => {
  const alerts = [];

  creditSources.forEach(source => {
    const stats = source.stats || calculateCreditStats(source);

    // Over limit alert
    if (stats.isOverLimit) {
      alerts.push({
        sourceId: source.id,
        sourceName: source.name,
        type: 'over-limit',
        severity: 'critical',
        message: `Vượt hạn mức ${formatMoney(stats.usedCredit - stats.creditLimit)} đ`,
        amount: stats.usedCredit - stats.creditLimit
      });
    }

    // High utilization alert
    if (stats.utilizationRate >= 90 && !stats.isOverLimit) {
      alerts.push({
        sourceId: source.id,
        sourceName: source.name,
        type: 'high-utilization',
        severity: 'critical',
        message: `Sử dụng ${stats.utilizationRate.toFixed(0)}% hạn mức`,
        utilizationRate: stats.utilizationRate
      });
    } else if (stats.utilizationRate >= 70) {
      alerts.push({
        sourceId: source.id,
        sourceName: source.name,
        type: 'high-utilization',
        severity: 'warning',
        message: `Sử dụng ${stats.utilizationRate.toFixed(0)}% hạn mức`,
        utilizationRate: stats.utilizationRate
      });
    }

    // Payment due soon alert
    if (stats.daysUntilDue !== null && stats.usedCredit > 0) {
      if (stats.daysUntilDue < 0) {
        alerts.push({
          sourceId: source.id,
          sourceName: source.name,
          type: 'payment-overdue',
          severity: 'critical',
          message: `Quá hạn thanh toán ${Math.abs(stats.daysUntilDue)} ngày`,
          daysOverdue: Math.abs(stats.daysUntilDue),
          minimumPayment: stats.minimumPayment
        });
      } else if (stats.daysUntilDue <= 3) {
        alerts.push({
          sourceId: source.id,
          sourceName: source.name,
          type: 'payment-due-soon',
          severity: 'critical',
          message: `Còn ${stats.daysUntilDue} ngày đến hạn thanh toán`,
          daysUntilDue: stats.daysUntilDue,
          minimumPayment: stats.minimumPayment
        });
      } else if (stats.daysUntilDue <= 7) {
        alerts.push({
          sourceId: source.id,
          sourceName: source.name,
          type: 'payment-due-soon',
          severity: 'warning',
          message: `Còn ${stats.daysUntilDue} ngày đến hạn thanh toán`,
          daysUntilDue: stats.daysUntilDue,
          minimumPayment: stats.minimumPayment
        });
      }
    }
  });

  // Sort by severity
  const severityOrder = { critical: 0, warning: 1, info: 2 };
  alerts.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);

  return alerts;
};

// Helper function for formatting (same as in components)
const formatMoney = (value) => {
  if (!value) return '0';
  if (value >= 1_000_000_000) return (value / 1_000_000_000).toFixed(1).replace('.0', '') + 'B';
  if (value >= 1_000_000) return (value / 1_000_000).toFixed(1).replace('.0', '') + 'M';
  if (value >= 1_000) return (value / 1_000).toFixed(0) + 'K';
  return value.toLocaleString();
};
